import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, TrendingDown, Users, Clock, DollarSign, CheckCircle, Calendar } from 'lucide-react';
import { DemoBanner } from '../../components/DemoBanner';
import { analytics } from '../../mockData';

const Analytics = () => {
  const facilityName = localStorage.getItem('userName') || 'Your Facility';
  const data = analytics['emp-001'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Recruitment Analytics</h1>
            <p className="text-gray-600">Track your hiring performance and ROI</p>
          </div>

          {/* Demo Banner */}
          <DemoBanner 
            type="demo"
            title="Sample Analytics Data"
            description="The metrics shown below are sample data for demonstration. Your actual recruitment analytics will populate as you use the platform to hire candidates."
          />

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Active Jobs</span>
                  <Users className="h-5 w-5 text-[#42b1c2]" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{data.activeJobs}</div>
                <div className="flex items-center text-sm text-green-600">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  <span>+12% from last month</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Time to Hire</span>
                  <Clock className="h-5 w-5 text-[#42b1c2]" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{data.averageTimeToHire} days</div>
                <div className="flex items-center text-sm text-green-600">
                  <TrendingDown className="h-4 w-4 mr-1" />
                  <span>-35% faster</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Cost Per Hire</span>
                  <DollarSign className="h-5 w-5 text-[#42b1c2]" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">SAR {data.costPerHire.toLocaleString()}</div>
                <div className="flex items-center text-sm text-green-600">
                  <TrendingDown className="h-4 w-4 mr-1" />
                  <span>-40% reduction</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Total Hires</span>
                  <CheckCircle className="h-5 w-5 text-[#42b1c2]" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{data.hires}</div>
                <div className="flex items-center text-sm text-green-600">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  <span>+25% this quarter</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Hiring Funnel */}
            <Card>
              <CardHeader>
                <CardTitle>Hiring Funnel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Total Applicants</span>
                      <span className="text-sm font-bold text-gray-900">{data.totalApplicants}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-[#42b1c2] h-3 rounded-full" style={{width: '100%'}}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Matched Candidates</span>
                      <span className="text-sm font-bold text-gray-900">{data.matchedCandidates}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-[#42b1c2] h-3 rounded-full" style={{width: '80%'}}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Interviews Scheduled</span>
                      <span className="text-sm font-bold text-gray-900">{data.interviewsScheduled}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-[#42b1c2] h-3 rounded-full" style={{width: '40%'}}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Offers Extended</span>
                      <span className="text-sm font-bold text-gray-900">15</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-[#42b1c2] h-3 rounded-full" style={{width: '25%'}}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Hires Completed</span>
                      <span className="text-sm font-bold text-gray-900">{data.hires}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-green-600 h-3 rounded-full" style={{width: '15%'}}></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Monthly Hiring Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.monthlyData.map((month) => (
                    <div key={month.month} className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <span className="text-sm font-medium text-gray-700 w-12">{month.month}</span>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs text-gray-600">Applicants: {month.applicants}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{width: `${(month.applicants / 100) * 100}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                      <Badge className="ml-4 bg-green-100 text-green-800">
                        {month.hires} hired
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quality Metrics */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Candidate Quality</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#42b1c2] mb-2">{data.candidateQuality}</div>
                  <p className="text-sm text-gray-600">out of 5 stars</p>
                  <p className="text-xs text-gray-500 mt-2">Based on post-hire reviews</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Interview-to-Hire Ratio</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#42b1c2] mb-2">67%</div>
                  <p className="text-sm text-gray-600">Success rate</p>
                  <p className="text-xs text-gray-500 mt-2">12 hires from 18 interviews</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">ROI Improvement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-600 mb-2">+156%</div>
                  <p className="text-sm text-gray-600">vs traditional methods</p>
                  <p className="text-xs text-gray-500 mt-2">Savings on recruitment costs</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cost Savings Breakdown */}
          <Card className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10">
            <CardHeader>
              <CardTitle>Cost Savings Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Traditional Recruitment</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Agency Fees (25%)</span>
                      <span className="font-medium">SAR 50,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Advertising</span>
                      <span className="font-medium">SAR 8,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Verification Services</span>
                      <span className="font-medium">SAR 6,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Staff Time</span>
                      <span className="font-medium">SAR 12,000</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t font-semibold text-base">
                      <span>Total per hire</span>
                      <span className="text-red-600">SAR 76,000</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Luma Med Global</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Platform Fee (15%)</span>
                      <span className="font-medium">SAR 30,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Included: AI Matching</span>
                      <span className="font-medium text-green-600">Free</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Included: Verification</span>
                      <span className="font-medium text-green-600">Free</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Included: Automation</span>
                      <span className="font-medium text-green-600">Free</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t font-semibold text-base">
                      <span>Total per hire</span>
                      <span className="text-green-600">SAR 30,000</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white rounded-lg text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">SAR 46,000 saved per hire</div>
                <p className="text-gray-600">Total savings this year: SAR 552,000</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Analytics;